import React, { useCallback, useEffect, useState } from 'react';
import { Building2, Share2, MapPin, BedDouble, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { EmptyState, fmtMoney } from './shared';
import { ReferralDialog } from './ReferralDialogs';

const imgSrc = (u) => (!u ? null : u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const MySharingDialog = ({ open, onClose, onSaved }) => {
  const [props, setProps] = useState([]);
  const [selected, setSelected] = useState(new Set());
  useEffect(() => {
    if (!open) return;
    Promise.all([api.get('/properties'), api.get('/partnerships/my-shared')])
      .then(([p, s]) => { setProps(p.data || []); setSelected(new Set(s.data || [])); })
      .catch(() => toast.error('Chargement impossible'));
  }, [open]);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const save = async () => {
    try {
      await api.put('/partnerships/my-shared', { property_ids: [...selected] });
      toast.success('Biens partagés mis à jour'); onClose(); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="my-sharing-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#2B3A2C]">Mes biens partagés</DialogTitle>
          <DialogDescription>Cochez les biens visibles par toutes vos agences partenaires (sans les infos du propriétaire).</DialogDescription>
        </DialogHeader>
        <div className="max-h-80 overflow-y-auto space-y-1.5">
          {props.map((p) => (
            <label key={p.id} className="flex items-center gap-3 rounded-xl border border-gray-100 p-2.5 cursor-pointer hover:bg-[#F5EFE1]/60" data-testid={`share-property-${p.id}`}>
              <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)} />
              <span className="flex-1 text-sm text-[#2B3A2C] truncate">{p.title}</span>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${['disponible', 'available', 'a_louer', 'a_vendre'].includes(p.status) ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{p.status}</span>
              <span className="text-[11px] text-gray-500">{p.city}</span>
            </label>
          ))}
        </div>
        <Button onClick={save} className="w-full bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-xl" data-testid="my-sharing-save">
          Enregistrer ({selected.size})
        </Button>
      </DialogContent>
    </Dialog>
  );
};

const PropertyCard = ({ p, canRefer, onRefer }) => (
  <div className="bg-white rounded-2xl border border-[#B85C38]/15 overflow-hidden shadow-sm" data-testid={`shared-property-${p.id}`}>
    <div className="h-36 bg-gradient-to-br from-[#2B3A2C] to-[#3E4F3F] relative">
      {imgSrc(p.images?.[0]) && <img src={imgSrc(p.images[0])} alt={p.title} className="w-full h-full object-cover" />}
      <Badge className="absolute top-2 left-2 bg-white/95 text-[#2B3A2C] border-0">{p.partner?.name}</Badge>
    </div>
    <div className="p-4 space-y-1.5">
      <p className="font-display font-bold text-[#2B3A2C] truncate">{p.title}</p>
      <p className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" />{[p.district, p.city].filter(Boolean).join(', ') || '—'}</p>
      <div className="flex items-center justify-between">
        <span className="font-bold text-[#B85C38]">{fmtMoney(p.price)}</span>
        {p.bedrooms ? <span className="text-xs text-gray-500 flex items-center gap-1"><BedDouble className="w-3 h-3" />{p.bedrooms} ch.</span> : null}
      </div>
      <p className="text-[11px] text-gray-500 capitalize">{p.transaction_type} · {p.property_type} · {p.status}</p>
      {canRefer && (
        <Button size="sm" onClick={() => onRefer(p)} className="w-full mt-2 bg-[#2B3A2C] hover:bg-[#1f2b20] text-white rounded-xl" data-testid={`refer-on-property-${p.id}`}>
          <Send className="w-3.5 h-3.5 mr-1" /> Proposer un client
        </Button>
      )}
    </div>
  </div>
);

const AutoShareToggle = ({ onChanged }) => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    api.get('/partnerships/settings').then((r) => setOn(!!r.data?.auto_share_available)).catch(() => {});
  }, []);
  const toggle = async (v) => {
    try {
      await api.put('/partnerships/settings', { auto_share_available: v });
      setOn(v); onChanged?.();
      toast.success(v ? 'Partage automatique activé' : 'Partage automatique désactivé');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  return (
    <label className="flex items-center gap-3 rounded-xl bg-white border border-[#B85C38]/15 px-4 py-2.5 cursor-pointer" data-testid="auto-share-toggle-row">
      <Switch checked={on} onCheckedChange={toggle} data-testid="auto-share-switch" />
      <span className="text-sm text-[#2B3A2C]">
        <b>Partage automatique</b> : tous mes biens disponibles sont visibles par mes partenaires
      </span>
    </label>
  );
};

export const SharedPropertiesTab = ({ isAdmin, active, reload }) => {
  const [props, setProps] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [referOn, setReferOn] = useState(null);
  const load = useCallback(() => {
    api.get('/partnerships/shared-properties').then((r) => setProps(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, active.length]);

  return (
    <div className="space-y-4">
      {isAdmin && (
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => setSharing(true)} className="bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-xl" data-testid="my-sharing-btn">
            <Share2 className="w-4 h-4 mr-1.5" /> Choisir mes biens partagés
          </Button>
          <AutoShareToggle />
        </div>
      )}
      {props.length === 0 ? <EmptyState icon={Building2} text="Vos partenaires n'ont encore partagé aucun bien." testid="shared-properties-empty" /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {props.map((p) => <PropertyCard key={p.id} p={p} canRefer={isAdmin} onRefer={setReferOn} />)}
        </div>
      )}
      <MySharingDialog open={sharing} onClose={() => setSharing(false)} onSaved={reload} />
      <ReferralDialog open={!!referOn} onClose={() => setReferOn(null)} active={active} property={referOn} />
    </div>
  );
};
